import { useEffect, useState } from "react";
import { Layout } from "../components/layout.tsx";
import { useAuth } from "../context/auth.tsx";
import { listDocs, publishDoc, type DocArtifact, type Page } from "../api/delivery.ts";

const STATUS_COLORS: Record<string, string> = {
  DRAFT: "bg-gray-700 text-gray-300",
  IN_REVIEW: "bg-yellow-900/50 text-yellow-300",
  PUBLISHED: "bg-green-900/50 text-green-300",
  ARCHIVED: "bg-gray-800 text-gray-500",
};

export function DeliveryDocsPage() {
  const { token } = useAuth();
  const [data, setData] = useState<Page<DocArtifact> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState<DocArtifact | null>(null);
  const [acting, setActing] = useState<string | null>(null);

  const load = () => {
    if (token == null) return;
    setLoading(true);
    listDocs(token, { type: typeFilter || undefined, status: statusFilter || undefined, limit: 50 })
      .then(setData)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Failed"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [token, typeFilter, statusFilter]);

  const publish = async (doc: DocArtifact) => {
    if (token == null) return;
    try {
      setActing(doc.id);
      await publishDoc(token, doc.id, doc.version);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed");
    } finally {
      setActing(null);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-white">Documentation</h1>

        <div className="flex gap-3 flex-wrap">
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="bg-gray-800 border border-gray-700 text-gray-200 rounded px-3 py-1.5 text-sm">
            <option value="">All Types</option>
            {["README", "API", "ARCHITECTURE", "DEPLOYMENT", "USER_GUIDE", "CHANGELOG", "OTHER"].map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-gray-800 border border-gray-700 text-gray-200 rounded px-3 py-1.5 text-sm">
            <option value="">All Statuses</option>
            {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {loading && <p className="text-gray-400">Loading…</p>}
        {error != null && <p className="text-red-400">{error}</p>}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-1 space-y-2">
            {data?.items.map((doc) => (
              <button key={doc.id} onClick={() => setSelected(doc)} className={`w-full text-left bg-gray-800/50 border rounded-lg p-3 transition-colors ${selected?.id === doc.id ? "border-blue-500" : "border-gray-700 hover:border-gray-600"}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-white text-sm font-medium truncate">{doc.title}</p>
                    <p className="text-gray-500 text-xs">{doc.type} · v{doc.docVersion}</p>
                  </div>
                  <span className={`text-xs px-1.5 py-0.5 rounded shrink-0 ${STATUS_COLORS[doc.status] ?? "bg-gray-700 text-gray-300"}`}>{doc.status}</span>
                </div>
              </button>
            ))}
            {data?.items.length === 0 && <p className="text-gray-400 text-sm">No documents.</p>}
          </div>

          <div className="md:col-span-2">
            {selected != null ? (
              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="text-white font-semibold">{selected.title}</h2>
                    <p className="text-gray-500 text-sm">{selected.type} · v{selected.docVersion} · {selected.status}</p>
                    {selected.generatedByAgent && <p className="text-xs text-blue-400 mt-0.5">Generated by: {selected.generatedByAgent}</p>}
                  </div>
                  {selected.status === "DRAFT" && (
                    <button disabled={acting != null} onClick={() => publish(selected)} className="px-3 py-1 text-xs bg-green-700 hover:bg-green-600 text-white rounded transition-colors disabled:opacity-50">Publish</button>
                  )}
                </div>
                <pre className="text-gray-300 text-sm whitespace-pre-wrap font-mono bg-gray-900 rounded p-4 max-h-96 overflow-y-auto">{selected.content}</pre>
              </div>
            ) : (
              <div className="bg-gray-800/30 border border-gray-700/50 rounded-lg p-8 text-center">
                <p className="text-gray-500">Select a document to view its content</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
